import React from 'react';
import { ArrowRight, CalendarDays, Check } from 'lucide-react';
import { Modal } from '../../../components/common/Modal';
import { useLanguage } from '../../../features/language/useLanguage';
import {
  openNativeDatePickerSafely,
  preventManualDateEntry,
  preventManualDatePasteOrDrop,
} from '../../../utils/date';

export interface CompleteSummaryLot {
  lotId: string;
  lotName: string;
  usedWeight: number;
}

export interface CompleteWorkOrderSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (expiryDate?: string) => void;
  submitting?: boolean;
  beanName?: string;
  lots: CompleteSummaryLot[];
  inputWeight: number;
  outputWeight: number;
  agtronWhole?: string;
  agtronGround?: string;
  agtronScaa?: string;
  agtronCommon?: string;
  roastTypeLabel?: string;
  initialExpiryDate?: string;
}

const formatKg = (value: number) => {
  if (!Number.isFinite(value) || value === 0) return '0.000';
  return value.toFixed(3);
};

export const CompleteWorkOrderSummaryModal: React.FC<CompleteWorkOrderSummaryModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  submitting = false,
  beanName,
  lots,
  inputWeight,
  outputWeight,
  agtronWhole,
  agtronGround,
  agtronScaa,
  agtronCommon,
  roastTypeLabel,
  initialExpiryDate,
}) => {
  const { t } = useLanguage();
  const [expiryDate, setExpiryDate] = React.useState<string>(initialExpiryDate ?? '');
  const expiryInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isOpen) {
      setExpiryDate(initialExpiryDate ?? '');
    }
  }, [isOpen, initialExpiryDate]);

  const totalLotWeight = lots.reduce(
    (sum, lot) => (Number.isFinite(lot.usedWeight) ? sum + lot.usedWeight : sum),
    0
  );

  const shrinkageKg = inputWeight > 0 && outputWeight > 0 ? inputWeight - outputWeight : 0;
  const shrinkagePercent = inputWeight > 0 && outputWeight > 0 ? (shrinkageKg / inputWeight) * 100 : 0;

  const hasAgtron = Boolean(agtronWhole || agtronGround);
  const agtronClassification = [agtronScaa, agtronCommon].filter(Boolean).join(' · ');

  const sections: Array<{ num: string; label: string }> = [
    { num: '01', label: t('workOrders.completeSummaryModal.section01') },
    { num: '02', label: t('workOrders.completeSummaryModal.section02') },
    { num: '03', label: t('workOrders.completeSummaryModal.section03') },
    { num: '04', label: t('workOrders.completeSummaryModal.section04') },
    { num: '05', label: t('workOrders.completeSummaryModal.section05') },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => undefined : onClose}
      title={t('workOrders.completeSummaryModal.title')}
      maxWidth="xl"
    >
      <div data-test-id="wo-complete-summary-modal">
        {/* Banner */}
        <div className="mb-5 flex items-baseline justify-between border-b border-gray-200 pb-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-primary-700">
            {t('workOrders.completeSummaryModal.banner')}
          </span>
          <span className="font-mono text-[10px] tabular-nums text-gray-400">
            {new Date().toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' })}
          </span>
        </div>

        {/* Headline + bean */}
        <div className="mb-6">
          <p className="text-sm text-gray-500">{t('workOrders.completeSummaryModal.headline')}</p>
          <p
            className="mt-1 text-xl font-semibold tracking-tight text-gray-900"
            data-test-id="wo-complete-summary-bean"
          >
            {beanName?.trim() ? beanName : t('workOrders.completeSummaryModal.noBean')}
          </p>
        </div>

        {/* Sections with continuous connecting line */}
        <div className="relative space-y-5 pl-10">
          <div
            className="pointer-events-none absolute left-3 top-3 bottom-3 w-px bg-primary-200"
            aria-hidden="true"
          />

          {/* 01 — Beans & Lots */}
          <section className="relative">
            <SectionMarker num={sections[0].num} />
            <SectionHeading label={sections[0].label} />
            {lots.length === 0 ? (
              <p className="mt-2 text-sm italic text-gray-400">
                {t('workOrders.completeSummaryModal.noLots')}
              </p>
            ) : (
              <ul className="mt-2 divide-y divide-gray-200">
                {lots.map((lot, idx) => (
                  <li
                    key={`${lot.lotId}-${idx}`}
                    className="flex items-baseline justify-between gap-4 py-1.5"
                  >
                    <span className="truncate text-sm text-gray-700">{lot.lotName}</span>
                    <span className="shrink-0 font-mono text-sm tabular-nums text-gray-900">
                      {formatKg(lot.usedWeight)}
                      <span className="ml-0.5 text-xs text-gray-400">kg</span>
                    </span>
                  </li>
                ))}
                <li className="flex items-baseline justify-between gap-4 pt-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {t('workOrders.completeSummaryModal.total')}
                  </span>
                  <span className="font-mono text-sm font-semibold tabular-nums text-primary-700">
                    {formatKg(totalLotWeight)}
                    <span className="ml-0.5 text-xs font-normal text-primary-600/70">kg</span>
                  </span>
                </li>
              </ul>
            )}
          </section>

          {/* 02 — Input → Output */}
          <section className="relative">
            <SectionMarker num={sections[1].num} />
            <SectionHeading label={sections[1].label} />
            <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
              <MetricBlock
                label={t('workOrders.completeSummaryModal.input')}
                value={formatKg(inputWeight)}
              />
              <ArrowRight className="h-4 w-4 shrink-0 text-primary-600" aria-hidden="true" />
              <MetricBlock
                label={t('workOrders.completeSummaryModal.output')}
                value={formatKg(outputWeight)}
                align="center"
              />
              <div className="hidden h-10 w-px bg-gray-200 sm:block" aria-hidden="true" />
              <ShrinkageBadge
                kg={shrinkageKg}
                percent={shrinkagePercent}
                label={t('workOrders.completeSummaryModal.shrinkage')}
              />
            </div>
          </section>

          {/* 03 — Agtron */}
          <section className="relative">
            <SectionMarker num={sections[2].num} />
            <SectionHeading label={sections[2].label} />
            {!hasAgtron ? (
              <p className="mt-2 text-sm italic text-gray-400">
                {t('workOrders.completeSummaryModal.noAgtron')}
              </p>
            ) : (
              <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2">
                <AgtronReading
                  label={t('workOrders.completeSummaryModal.wholeBean')}
                  value={agtronWhole}
                />
                <AgtronReading
                  label={t('workOrders.completeSummaryModal.groundBean')}
                  value={agtronGround}
                />
                {agtronClassification && (
                  <span className="ml-auto inline-flex items-center rounded-full border border-primary-200 bg-primary-50 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wider text-primary-800">
                    {agtronClassification}
                  </span>
                )}
              </div>
            )}
          </section>

          {/* 04 — Roast Type */}
          <section className="relative">
            <SectionMarker num={sections[3].num} />
            <SectionHeading label={sections[3].label} />
            <div className="mt-2">
              {roastTypeLabel ? (
                <span
                  className="inline-flex items-center rounded-md bg-primary-600 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white"
                  data-test-id="wo-complete-summary-roasttype"
                >
                  {roastTypeLabel}
                </span>
              ) : (
                <span className="text-sm italic text-gray-400">
                  {t('workOrders.completeSummaryModal.noRoastType')}
                </span>
              )}
            </div>
          </section>

          {/* 05 — Expiry Date */}
          <section className="relative">
            <SectionMarker num={sections[4].num} />
            <div className="flex items-baseline justify-between gap-3">
              <SectionHeading label={sections[4].label} />
              <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">
                {t('workOrders.completeSummaryModal.expiryOptional')}
              </span>
            </div>
            <div className="mt-2">
              <div
                className={`group relative flex items-center gap-3 rounded-lg border bg-white pl-3 pr-2 py-2 transition-all duration-200 ${
                  expiryDate
                    ? 'border-primary-300 ring-2 ring-primary-500/10'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => {
                  if (submitting) return;
                  expiryInputRef.current?.focus();
                  expiryInputRef.current?.click();
                }}
              >
                <CalendarDays
                  className={`h-4 w-4 shrink-0 ${expiryDate ? 'text-primary-600' : 'text-gray-400'}`}
                  aria-hidden="true"
                />
                <input
                  ref={expiryInputRef}
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  onClick={openNativeDatePickerSafely}
                  onKeyDown={preventManualDateEntry}
                  onPaste={preventManualDatePasteOrDrop}
                  onDrop={preventManualDatePasteOrDrop}
                  disabled={submitting}
                  min={new Date().toISOString().slice(0, 10)}
                  className="flex-1 border-0 bg-transparent p-0 font-mono text-sm tabular-nums text-gray-900 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
                  data-test-id="wo-complete-summary-expiry-input"
                />
                {expiryDate && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpiryDate('');
                    }}
                    disabled={submitting}
                    className="ml-1 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                    data-test-id="wo-complete-summary-expiry-clear"
                  >
                    {t('workOrders.completeSummaryModal.expiryClear')}
                  </button>
                )}
              </div>
              <p className="mt-1.5 text-[11px] leading-relaxed text-gray-500">
                {t('workOrders.completeSummaryModal.expiryHelp')}
              </p>
            </div>
          </section>
        </div>

        {/* Notice */}
        <p className="mt-6 border-l-2 border-primary-400 bg-primary-50 px-3 py-2 text-xs text-primary-900">
          {t('workOrders.completeSummaryModal.notice')}
        </p>

        {/* Actions */}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            data-test-id="wo-complete-summary-cancel"
          >
            {t('workOrders.completeSummaryModal.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(expiryDate || undefined)}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-60"
            data-test-id="wo-complete-summary-confirm"
          >
            <Check className="h-4 w-4" aria-hidden="true" />
            {submitting
              ? t('workOrders.completeSummaryModal.confirming')
              : t('workOrders.completeSummaryModal.confirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const SectionMarker: React.FC<{ num: string }> = ({ num }) => (
  <div className="absolute -left-10 top-0 flex h-6 w-6 items-center justify-center rounded-full bg-white ring-1 ring-primary-300">
    <span className="font-mono text-[11px] font-semibold tabular-nums text-primary-700">{num}</span>
  </div>
);

const SectionHeading: React.FC<{ label: string }> = ({ label }) => (
  <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-600">{label}</h4>
);

const MetricBlock: React.FC<{ label: string; value: string; align?: 'left' | 'center' | 'right' }> = ({
  label,
  value,
  align = 'left',
}) => (
  <div className={`flex flex-col ${align === 'center' ? 'items-center' : align === 'right' ? 'items-end' : 'items-start'}`}>
    <span className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{label}</span>
    <span className="font-mono text-lg font-semibold leading-tight tracking-tight text-gray-900 tabular-nums">
      {value}
      <span className="ml-0.5 text-xs font-sans font-normal text-gray-400">kg</span>
    </span>
  </div>
);

const AgtronReading: React.FC<{ label: string; value?: string }> = ({ label, value }) => {
  const display = value && value.trim() !== '' ? value : '—';
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</span>
      <span className="font-mono text-lg font-semibold tabular-nums text-gray-900">{display}</span>
    </div>
  );
};

const ShrinkageBadge: React.FC<{ kg: number; percent: number; label: string }> = ({ kg, percent, label }) => {
  if (kg <= 0) return null;
  return (
    <div className="flex flex-col items-end">
      <span className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{label}</span>
      <span className="font-mono text-sm font-semibold tabular-nums text-primary-700">
        −{formatKg(kg)}
        <span className="ml-0.5 text-xs font-normal text-primary-600/70">kg</span>
        <span className="ml-1.5 rounded bg-primary-100 px-1 py-0.5 text-[10px] font-semibold text-primary-800">
          {percent.toFixed(1)}%
        </span>
      </span>
    </div>
  );
};
